import layout from './agentLayout.module.css';
import React, { useState } from 'react';
import { Col, Input, Form } from 'antd';
import type { FormInstance } from 'antd';
import PhoneInput from 'react-phone-input-2';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { RouteConditionSelect } from './RouteConditionSelect';
import { DeleteIcon, PencilIcon } from '@/assets/icons/icons';
import type {
  AgentDrawerFormValues,
  EscalationRule,
  EscalateWhenValue,
  PhoneTree,
} from '../types';

export const EscalationRuleRow: React.FC<{
  rule: EscalationRule;
  form: FormInstance<AgentDrawerFormValues>;
  escalateWhenOptions: Array<{ value: string; label: string }>;
  loadingDropdowns: boolean;
  isDefaultAgent: boolean;
  readOnly?: boolean;
  required?: boolean;
  showErrors?: boolean;
  onChange: (updated: EscalationRule) => void;
  onDelete: (id: string) => void;
}> = ({
  rule,
  form,
  escalateWhenOptions,
  loadingDropdowns,
  isDefaultAgent,
  readOnly = false,
  required = false,
  showErrors = false,
  onChange,
  onDelete,
}) => {
  const [phone, setPhone] = useState<string>(
    rule.phone_number.replace(/^\+/, '')
  );
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [draftName, setDraftName] = useState<string>(rule.name);

  const commitName = () => {
    setIsEditingName(false);
    const trimmed = draftName.trim() || rule.name;
    setDraftName(trimmed);
    onChange({ ...rule, name: trimmed });
  };

  const phoneValidator = (value: string): string | null => {
    const v = value.trim();
    if (!v) return required ? 'Please enter a phone number' : null;
    const normalized = v.startsWith('+') ? v : `+${v}`;
    try {
      if (!isValidPhoneNumber(normalized)) return 'Invalid phone number';
    } catch {
      return 'Invalid phone number';
    }
    const digits = v.replace(/[^\d]/g, '');
    if (digits.startsWith('1') && digits.includes('555')) {
      return 'Please enter a real phone number';
    }
    return null;
  };

  const menu: PhoneTree | undefined = Form.useWatch('phone_tree', form);
  const hasKey = menu?.options?.some(
    (option) => option.destination === rule.id
  );

  const phoneError = phoneValidator(phone);
  const escalateWhenError =
    required && !isDefaultAgent && !hasKey && rule.escalate_when.length === 0
      ? 'Please select at least one escalation trigger'
      : null;

  return (
    <Col span={24} style={{ padding: 0 }}>
      <div className='escalationRuleRow__header'>
        {isEditingName ? (
          <Input
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commitName}
            onPressEnter={commitName}
            className='escalationRuleRow__nameInput'
          />
        ) : (
          <>
            <span
              className='escalationRuleRow__name'
              onClick={() => !readOnly && setIsEditingName(true)}
            >
              {rule.name}
            </span>
            {!readOnly && (
              <span
                className='escalationRuleRow__editIcon'
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingName(true);
                }}
              >
                <PencilIcon color='var(--text-primary)' />
              </span>
            )}
          </>
        )}
        {!readOnly && (
          <span
            className='escalationRuleRow__deleteIcon'
            onClick={() => onDelete(rule.id)}
          >
            <DeleteIcon />
          </span>
        )}
      </div>

      <div className='escalationRuleRow__body'>
        {!isDefaultAgent && (
          <div className='agentFormItem'>
            <label className='ant-form-item-label escalationRuleRow__label'>
              {menu?.enabled
                ? 'Route / Escalate when caller...'
                : 'Escalate when'}
            </label>
            <RouteConditionSelect
              form={form}
              destination={rule.id}
              options={escalateWhenOptions}
              disabled={readOnly || loadingDropdowns}
              value={rule.escalate_when}
              onChange={(vals: EscalateWhenValue[]) =>
                onChange({ ...rule, escalate_when: vals })
              }
            />
            {showErrors && escalateWhenError && (
              <div className='ant-form-item-explain-error'>
                {escalateWhenError}
              </div>
            )}
          </div>
        )}

        <div className={layout.phoneRow}>
          <div
            className={['agentFormItem', layout.grow].filter(Boolean).join(' ')}
          >
            <label className='ant-form-item-label escalationRuleRow__label'>
              Escalation phone number
            </label>
            <PhoneInput
              country='us'
              disableCountryCode={!phone}
              value={phone}
              onChange={(value) => {
                setPhone(value);
                onChange({ ...rule, phone_number: value ? `+${value}` : '' });
              }}
              disabled={readOnly}
              containerClass={
                readOnly ? 'agentPhone agentPhone--primary' : 'agentPhone'
              }
              inputClass={
                readOnly
                  ? 'agentPhone__input agentPhone__input--primary'
                  : 'agentPhone__input'
              }
              buttonClass={
                readOnly
                  ? 'agentPhone__button agentPhone__button--primary'
                  : 'agentPhone__button'
              }
              dropdownClass='agentPhone__dropdown'
            />
            {showErrors && phoneError && (
              <div className='ant-form-item-explain-error'>{phoneError}</div>
            )}
          </div>

          {(!readOnly || rule.extension) && (
            <div
              className={['agentFormItem', layout.extension]
                .filter(Boolean)
                .join(' ')}
            >
              <label
                className={[
                  'ant-form-item-label escalationRuleRow__label',
                  layout.hiddenLabel,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                &nbsp;
              </label>
              <Input
                className={
                  readOnly ? 'agentInput agentInput--readonly' : 'agentInput'
                }
                value={rule.extension ?? ''}
                onChange={(e) =>
                  onChange({ ...rule, extension: e.target.value })
                }
                disabled={readOnly}
                placeholder='Extension'
                maxLength={20}
              />
            </div>
          )}
        </div>
      </div>
    </Col>
  );
};
